import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, GraduationCap, Building2, Loader2 } from 'lucide-react';
import { partnerApi } from '../services/partner-api';
import { handleApiError } from '../lib/api-client';
import { CopyButton } from '../components/common/CopyButton';
import { EntityBadge } from '../components/common/EntityBadge';
import { QRCodeCard } from '../components/qr/QRCodeCard';
import type { PartnerTrainerDetail as TrainerDetailType } from '../types';

export function TrainerDetail() {
  const { id } = useParams<{ id: string }>();
  const [trainer, setTrainer] = useState<TrainerDetailType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    partnerApi.getTrainer(id)
      .then(setTrainer)
      .catch((err) => setError(handleApiError(err)))
      .finally(() => setIsLoading(false));
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={24} className="animate-spin text-blue-500" />
      </div>
    );
  }

  if (error || !trainer) {
    return (
      <div>
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft size={16} /> Powrot do wyszukiwarki
        </Link>
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm">
          {error || 'Nie znaleziono trenera'}
        </div>
      </div>
    );
  }

  return (
    <div>
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6">
        <ArrowLeft size={16} /> Powrot do wyszukiwarki
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Header */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-start gap-4">
              <EntityBadge type="trainer" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{trainer.displayName}</h1>
                {trainer.specializations.length > 0 && (
                  <p className="text-gray-500 mt-1">{trainer.specializations.join(', ')}</p>
                )}
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Dane</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <div>
                <dt className="text-gray-500">Typ</dt>
                <dd className="font-medium text-gray-900">Trener</dd>
              </div>
              {trainer.specializations.length > 0 && (
                <div>
                  <dt className="text-gray-500">Specjalizacje</dt>
                  <dd className="font-medium text-gray-900 flex items-center gap-1">
                    <GraduationCap size={14} /> {trainer.specializations.join(', ')}
                  </dd>
                </div>
              )}
              {trainer.city && (
                <div>
                  <dt className="text-gray-500">Miasto</dt>
                  <dd className="font-medium text-gray-900">{trainer.city}</dd>
                </div>
              )}
              {trainer.experienceYears != null && (
                <div>
                  <dt className="text-gray-500">Doswiadczenie</dt>
                  <dd className="font-medium text-gray-900">{trainer.experienceYears} lat</dd>
                </div>
              )}
              <div>
                <dt className="text-gray-500">Treningi</dt>
                <dd className="font-medium text-gray-900">{trainer.trainingsCount}</dd>
              </div>
              <div>
                <dt className="text-gray-500">ID Spotto</dt>
                <dd className="font-mono text-xs text-gray-600 break-all">{trainer.id}</dd>
              </div>
            </dl>
          </div>

          {/* Deep Link */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="font-semibold text-gray-900 mb-3">Deep Link (POS)</h2>
            <div className="bg-gray-50 rounded-lg p-3 font-mono text-sm text-gray-700 break-all">
              {trainer.deepLink}
            </div>
            <div className="mt-3">
              <CopyButton text={trainer.deepLink} label="Kopiuj link" />
            </div>
          </div>

          {/* Associated Businesses */}
          {trainer.associatedBusinesses.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="font-semibold text-gray-900 mb-4">
                Powiazane obiekty ({trainer.associatedBusinesses.length})
              </h2>
              <ul className="space-y-2">
                {trainer.associatedBusinesses.map((b) => (
                  <li key={b.id} className="flex items-center gap-2 text-sm">
                    <Building2 size={14} className="text-gray-400" />
                    <span className="text-gray-900">{b.displayName}</span>
                    <span className="text-gray-400">({b.city})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* QR Code sidebar */}
        <div>
          <QRCodeCard
            url={trainer.deepLink}
            entityName={trainer.displayName}
            entityType="trainer"
            entityId={trainer.id}
          />
        </div>
      </div>
    </div>
  );
}
